// Razorpay types
export interface RazorpayPaymentSuccess {
    razorpay_payment_id: string;
    razorpay_subscription_id: string;
    razorpay_signature: string;
}

export interface RazorpayPaymentFailure {
    error: {
        code: string;
        description: string;
        source: string;
        step: string;
        reason: string;
        metadata: {
            payment_id?: string;
            order_id?: string;
        };
    };
}

export interface RazorpayOptions {
    key: string;
    subscription_id: string;
    name: string;
    description?: string;
    image?: string;
    handler: (response: RazorpayPaymentSuccess) => void | Promise<void>;
    prefill?: {
        name?: string;
        email?: string;
        contact?: string;
    };
    notes?: Record<string, string>;
    theme?: { color?: string };
    modal?: {
        ondismiss?: () => void;
    };
    [extra: string]: unknown;
}

export interface RazorpayInstance {
    open(): void;
    on(
        event: "payment.failed",
        handler: (response: RazorpayPaymentFailure) => void
    ): void;
}

// Alias for the Razorpay JS constructor
export type RazorpayConstructor = new (
    options: RazorpayOptions
) => RazorpayInstance;

// Global augmentation for Razorpay
declare global {
    interface Window {
        Razorpay: RazorpayConstructor;
    }
}

// API data models
export interface ApiPlan {
    id: string;
    name: string;
    amount: number;
    currency: string;
    description: string;
    period: string; // e.g., 'month'
    interval: number;
    created_at: number;
}

export interface PlansResponse {
    success: boolean;
    plans: ApiPlan[];
}

export interface SubscriptionLinkResponse {
    success: boolean;
    subscription_url?: string;
    subscription_id?: string;
    customer_id?: string;
    subscription_status?: string;
    plan_details?: {
        id: string;
        name: string;
        amount: number;
        currency: string;
        description: string;
        period: string;
        interval: number;
    };
    user_details?: {
        name?: string;
        email?: string;
        contact?: string;
    };
    next_billing_at?: string;
    error?: string;
}

export interface CreateSubscriptionResponse {
    success: boolean;
    subscription?: {
        id: string;
        plan_id: string;
        status: string;
    };
    customer?: {
        id: string;
        name: string;
        email: string;
        contact: string;
    };
    session_id?: string;
    session_error?: string;
    error?: string;
    message?: string;
}

// UI Plan model for AllPlans
export interface UiPlan {
    id: string;
    name: string;
    description: string;
    price: string;
    suitableFor: string;
    features: string[];
    costNote: string;
    emoji: string;
    tag: string;
    highlight: boolean;
}

// CreateSubscription component types
export type PlanName = "Thrive Core" | "Thrive Plus" | "Thrive Pro";

export interface PlanDetails {
    headline: string;
    subheadline?: string;
    highlights: string[];
    features: string[];
    suitableFor: string[];
    pricing: string;
    costNote: string;
    legalDisclaimer: string;
    ctaLabel: string;
    results?: string;
    sectionalDisclaimers?: string[];
    folds: {
        fold1: {
            headline: string;
            subheadline: string;
            cta: string;
            notes: {
                ctaDesignNote: string;
            };
            disclaimers: string[];
        };
        fold3: {
            headline: string;
            subheadline: string;
            lhs: {
                headline: string;
                bullets: string[];
            };
            rhs: {
                headline: string;
                bullets: string[];
            };
            disclaimers: string[];
        };
        fold5: {
            headline: string;
            points: string[];
            disclaimers: string[];
            cards?: Array<{
                heading: string;
                image: string;
                points: Array<{
                    title: string;
                    description: string;
                }>;
                disclaimer?: string;
            }>;
        };
    };
}

export const planDetailsCopy: Record<PlanName, PlanDetails> = {
    "Thrive Core": {
        headline: "Lifestyle Change Program",
        subheadline:
            "Evidence-based weight management and diabetes prevention with expert coaching, personalized nutrition, and sustainable habit-building. No medication required.",
        highlights: [
            "Personalized nutrition & movement protocols",
            "Weekly expert coaching",
            "Indian-food–optimized meal planning",
            "Proven strategies for prevention & long-term wellness",
        ],
        features: [
            "Personalized nutrition & movement protocols",
            "Weekly coaching sessions",
            "Indian-food–optimized meal planning",
            "Science-backed prevention & wellness strategies",
        ],
        suitableFor: [
            "Anyone looking to lose weight, prevent diabetes, or build healthier habits naturally",
            "Individuals seeking sustainable, science-backed results through behaviour change",
        ],
        pricing: "₹3,245/month",
        costNote:
            "All features included. Doctor consultations and medications are not part of this plan.",
        results:
            "Improve weight, energy, metabolic health, and reduce diabetes risk through sustainable lifestyle change.",
        ctaLabel: "Start Subscription",
        legalDisclaimer:
            " CDC recognition applies to HabitNu's U.S. operations under the National Diabetes Prevention Program. Proven in the U.S., personalized for India.   Success depends on individual effort, adherence and health conditions. Results may vary.",
        folds: {
            fold1: {
                headline: "Progress Weighing On Your Mind?",
                subheadline:
                    "No worries, we’ve got you covered! Every HabitNu plan comes with a free smart scale. More than a scale, it’s your daily reminder that small steps lead to big results.",
                cta: "Thrive > Survive",
                notes: {
                    ctaDesignNote:
                        "Make the ‘greater than’ sign (>) visually prominent",
                },
                disclaimers: [
                    " Personalized coaching does not replace medical care.",
                    "  Success depends on individual effort, adherence and health conditions. Results may vary.",
                    "   No medication required; program focuses on lifestyle-driven transformation.",
                ],
            },
            fold3: {
                headline: "The Thrive Core Difference",
                subheadline:
                    "Generic wellness apps give you tips. HabitNu gives you clinically trained dietitians and coaches who understand how GLP-1 works with your body.",
                lhs: {
                    headline: "HabitNu Transformation Program  ",
                    bullets: [
                        "Certified Prevention Specialists — Dietitians trained in diabetes reversal, not generic wellness",
                        "Culturally-Adapted Nutrition Plans — Indian food database, regional cuisines, lifestyle integration",
                        "Personal Expert Accountability — Dedicated dietitian who knows your goals, challenges, history",
                        "Sustainable Habit Architecture — Holistic behavior transformation, not temporary restrictions",
                        "Measurable Health Outcomes  — A1C reduction, weight loss, risk elimination: tracked and verified",
                    ],
                },
                rhs: {
                    headline: "Generic Apps",
                    bullets: [
                        "No Clinical Specialization — General wellness advice without clinical prevention expertise",
                        "Western-Centric Food Database — Limited Indian food options, no cultural adaptation",
                        "Automated Bot Responses — Automated reminders without expert problem-solving",
                        "Basic Tracking Only — Counts calories but doesn't address root behaviours",
                        "No Outcome Accountability — Engagement metrics replace actual health transformation",
                    ],
                },
                disclaimers: [
                    " Success depends on individual effort, adherence, and health conditions. Results may vary.",
                ],
            },
            fold5: {
                headline: "Your Complete Transformation Program ",
                points: [
                    "Sustainable weight management through lifestyle change",
                    "Improved energy, sleep, and overall metabolic health",
                    "Reduced diabetes risk with long-term behaviour change",
                ],
                disclaimers: [
                    " Success depends on individual effort, adherence and health conditions. Results may vary.",
                ],
                cards: [
                    {
                        heading: "Personalized Expertise",
                        image: "/images/cards/core-expertise.png",
                        points: [
                            {
                                title: "Dedicated Prevention Coach",
                                description:
                                    "Expert dietitian trained in evidence-based prevention methodology  ",
                            },
                            {
                                title: "Structured Support System",
                                description:
                                    "Weekly coaching sessions building accountability and progress",
                            },
                            {
                                title: "Peer Support Network",
                                description:
                                    "Community sessions with shared goals and encouragement",
                            },
                        ],
                        disclaimer:
                            "  Coaching does not replace medical care. For side-effect concerns, consult your healthcare provider.",
                    },
                    {
                        heading: "Intelligent Technology",
                        image: "/images/cards/core-technology.png",
                        points: [
                            {
                                title: "WhatsApp-Integrated Platform",
                                description:
                                    "Real-time support via your everyday messaging app",
                            },
                            {
                                title: "Comprehensive Health Dashboards",
                                description:
                                    "Track weight, habits, A1C and transformation milestones",
                            },
                            {
                                title: "Habi AI Companion",
                                description:
                                    "Smart nudges and motivation adapted to your journey",
                            },
                        ],
                    },
                    {
                        heading: "Culturally-Adapted Resources",
                        image: "/images/cards/core-resources.png",
                        points: [
                            {
                                title: "Indian Food Intelligence",
                                description:
                                    "Traditional cuisines optimised for metabolic health",
                            },
                            {
                                title: "Lifestyle Integration Modules",
                                description:
                                    "Movement, stress, sleep strategies for holistic wellness",
                            },
                            {
                                title: "Regional Recipe Library",
                                description:
                                    "From dosa to biryani, adapted to your goals",
                            },
                        ],
                    },
                    {
                        heading: "Measurable Outcomes",
                        image: "/images/cards/core-outcomes.png",
                        points: [
                            {
                                title: "Evidence-Based Weight Loss ",
                                description:
                                    "U.S. participants average 5–7% sustained weight reduction",
                            },
                            {
                                title: "Diabetes Risk Reduction ",
                                description:
                                    "U.S. participants reduced 54% prediabetes remission via lifestyle change",
                            },
                            {
                                title: "Lasting Habit Development ",
                                description:
                                    "Habits that transform you for life beyond any program",
                            },
                        ],
                    },
                ],
            },
        },
    },

    "Thrive Plus": {
        headline: "Medication Optimization Program",
        subheadline:
            "For people already using GLP-1: maximize results with physician oversight, advanced side-effect support, and muscle-preserving nutrition strategies.",
        highlights: [
            "Clinical oversight from specialised physicians",
            "Advanced side-effect management",
            "Muscle-preservation nutrition protocols",
            "Designed for current GLP-1 users",
        ],
        features: [
            "Physician oversight & monitoring",
            "Side-effect management protocols",
            "Muscle-preserving nutrition strategies",
            "Optimized support for GLP-1 medication users",
        ],
        suitableFor: [
            "Current GLP-1 users who want expert guidance to optimize results",
            "Individuals looking for long-term sustainability beyond medication",
        ],
        pricing: "₹4,425/month",
        costNote:
            "If GLP-1 is prescribed and delivery is chosen, independent distributor charges apply.",
        results:
            "Improved medication effectiveness, reduced side effects, and sustainable long-term outcomes.",
        ctaLabel: "Start Subscription",
        legalDisclaimer:
            " Coaching does not replace medical care. For side-effect concerns, consult your healthcare provider. In the event of any medical emergency or serious side effects, users should immediately seek medical attention or go to the nearest hospital. Our dietitians are trained in supporting lifestyle and nutrition for individuals who use GLP-1 medications. They do not provide medical prescriptions.   Success depends on individual effort, adherence and health conditions. Results may vary.",
        folds: {
            fold1: {
                headline: "Progress Weighing On Your Mind?",
                subheadline:
                    "No worries, we’ve got you covered! Every HabitNu plan comes with a free smart scale. More than a scale, it’s your daily reminder that small steps lead to big results.",
                cta: "Thrive > Survive",
                notes: {
                    ctaDesignNote:
                        "Make the ‘greater than’ sign (>) visually prominent",
                },
                disclaimers: [
                    " Coaching does not replace medical care.",
                    "  For any side-effect concerns, consult your healthcare provider.",
                    "   Success depends on individual effort, adherence, and health conditions. Results may vary.",
                ],
            },
            fold3: {
                headline: "The Thrive Plus Difference",
                subheadline:
                    "GLP-1 alone isn't enough. Without expert oversight and lifestyle integration, medication outcomes can plateau, side effects may persist and muscle loss accelerates.  We provide specialized support   that helps optimize your GLP-1 experience, backed by 85,000+ proven outcomes in the U.S.   ",
                lhs: {
                    headline: "Thrive Plus: Complete GLP-1 Support ",
                    bullets: [
                        "End-to-End Physician Partnership — Consultation, evaluation, prescription (if appropriate) and ongoing optimisation",
                        "Coordinated Medication Access — Seamless connection to independent licensed distributors for fulfillment (where prescribed)",
                        "Dedicated Care Coordination — Personal advocate managing all aspects of your GLP-1 care journey  ",
                        "Comprehensive Lifestyle Integration — Nutrition, movement, stress, sleep. Complete behavioural foundation.",
                        "Post-Medication Planning  — Strategies supporting sustainable habits beyond medication use",
                    ],
                },
                rhs: {
                    headline: "Fragmented GLP-1 Management",
                    bullets: [
                        "Disconnected Care Providers — Finding doctors, managing prescriptions, coordinating refills separately",
                        "Medication Access Barriers — Navigating distributors, supply chains and logistics independently",
                        "No Care Coordination — Managing physician, pharmacy and nutrition support without integration",
                        "Lifestyle Afterthought — Medication focus without behavioural habit formation for sustainability",
                        "Post-Therapy Weight Regain — No transition planning after stopping medication therapy",
                    ],
                },
                disclaimers: [
                    " Coaching does not replace medical care. For side-effect concerns, consult your healthcare provider.",
                    "  Dietitians are trained to support GLP-1 users but do not prescribe medications.",
                    "   Success depends on individual effort, adherence, and health conditions. Results may vary.",
                ],
            },
            fold5: {
                headline: "Your Complete GLP-1 Care Program ",
                points: [
                    "Better medication effectiveness through personalized nutrition",
                    "Reduced side effects with continuous monitoring",
                    "Sustainable results with behavioral coaching support",
                ],
                disclaimers: [
                    " Success depends on individual effort, adherence, and health conditions. Results may vary.",
                ],
                cards: [
                    {
                        heading: "Physician Consultation & Coordination",
                        image: "/images/plus-physician-consultation.png",
                        points: [
                            {
                                title: "Comprehensive Medical Evaluation",
                                description:
                                    "Complete assessment determining GLP-1 appropriateness and safety.",
                            },
                            {
                                title: "Prescription & Treatment Planning",
                                description:
                                    "Evidence-based recommendations and medication initiation (if appropriate).",
                            },
                            {
                                title: "Ongoing Medical Optimization",
                                description:
                                    "Continuous monitoring, dosage adjustments and therapeutic management.",
                            },
                        ],
                    },
                    {
                        heading: "Coordinated Medication Access",
                        image: "/images/plus-medication-access.png",
                        points: [
                            {
                                title: "Independent Distributor Connections",
                                description:
                                    "Seamless coordination with licensed providers for medication fulfillment.",
                            },
                            {
                                title: "Delivery & Refill Management",
                                description:
                                    "Logistics support ensuring consistent, timely medication access.",
                            },
                            {
                                title: "Supply Chain Advocacy",
                                description:
                                    "Dedicated coordination resolving access barriers and supply challenges.",
                            },
                        ],
                    },
                    {
                        heading: "Comprehensive Lifestyle Integration",
                        image: "/images/plus-lifestyle-integration.png",
                        points: [
                            {
                                title: "GLP-1-Specialized Nutrition Protocols",
                                description:
                                    "Muscle-preserving, culturally-adapted meal plans optimizing medication response.",
                            },
                            {
                                title: "WhatsApp-Integrated Guidance",
                                description:
                                    "Real-time support for side effects, nutrition questions, challenges.",
                            },
                            {
                                title: "Behavioural Science Coaching",
                                description:
                                    "Habit formation ensuring results last beyond medication.",
                            },
                        ],
                    },
                    {
                        heading: "Dedicated Care Coordination",
                        image: "/images/plus-care-coordination.png",
                        points: [
                            {
                                title: "Personal Care Navigator",
                                description:
                                    "Single point of contact managing physician, distribution and coaching alignment.",
                            },
                            {
                                title: "Proactive Side-Effect Management",
                                description:
                                    "Coordinated physician-dietitian protocols minimizing medication discomfort.",
                            },
                            {
                                title: "Post-Therapy Success Planning",
                                description:
                                    "Comprehensive strategies preventing weight regain after medication completion.",
                            },
                        ],
                    },
                ],
            },
        },
    },

    "Thrive Pro": {
        headline: "Complete GLP-1 Care",
        subheadline:
            "Full GLP-1 support: physician oversight, medication coordination, expert coaching, and long-term lifestyle guidance for sustainable health outcomes.",
        highlights: [
            "Medical evaluation & continuous monitoring",
            "Coordinated medication access via licensed partners",
            "Expert coaching & dedicated care coordinator",
            "Transition planning for long-term maintenance",
        ],
        features: [
            "Medical evaluation & ongoing monitoring",
            "Medication coordination through licensed distributors",
            "Expert coaching & dedicated care support",
            "Long-term transition & maintenance strategies",
        ],
        suitableFor: [
            "GLP-1 users seeking fully managed care including medical oversight, coaching, medication access, and continuous progress tracking",
        ],
        pricing: "₹4,425/month + Cost of Medication(15 -20K/month)",
        costNote:
            "₹4,425 charged upfront for consultation + coaching. Medication billed separately if prescribed after consultation.",
        results:
            "Safe, coordinated GLP-1 care designed to maximize results and support long-term health.",
        ctaLabel: "Start Subscription",
        legalDisclaimer:
            " Coaching does not replace medical care. For side-effect concerns, consult your healthcare provider.   In the event of any medical emergency or serious side effects, users should immediately seek medical attention or go to the nearest hospital.    Our dietitians are trained in supporting lifestyle and nutrition for individuals who use GLP-1 medications. They do not provide medical prescriptions.     Success depends on individual effort, adherence and health conditions. Results may vary.",
        folds: {
            fold1: {
                headline: "Progress Weighing On Your Mind?",
                subheadline:
                    "No worries, we’ve got you covered! Every HabitNu plan comes with a free smart scale. More than a scale, it’s your daily reminder that small steps lead to big results.",
                cta: "Thrive > Survive",
                notes: {
                    ctaDesignNote:
                        "Make the ‘greater than’ sign (>) visually prominent",
                },
                disclaimers: [
                    " Success depends on individual effort, adherence, and health conditions. Results may vary.",
                    "  Coaching does not replace medical care. For side-effect concerns, consult your healthcare provider.",
                    "   Our dietitians are trained in supporting lifestyle and nutrition for individuals who use GLP-1 medications. They do not provide medical prescriptions.",
                    "    In the event of any medical emergency or serious side effects, users should immediately seek medical attention or go to the nearest hospital.",
                    "     Medication coordination available where prescribed. Individual outcomes vary.",
                ],
            },
            fold3: {
                headline: "The Thrive Pro Difference",
                subheadline:
                    "Thrive Pro offers the most comprehensive GLP-1 care — integrating physician oversight, medication coordination, and specialized coaching for total lifestyle transformation. ",
                lhs: {
                    headline: "Thrive Pro: Complete GLP-1 Care",
                    bullets: [
                        "Medical Oversight Physician — Guidance supporting safe, effective medication use",
                        "GLP-1 Side-Effect Management — Proactive protocols for nausea, fatigue and digestive challenges  ",
                        "Muscle-Preserving Nutrition  — Specialised meal plans supporting muscle preservation during weight loss",
                        "Medication Journey Coaching   — Expert guidance through titration, plateaus and habit building",
                        "Post-Medication Planning    — Strategies for sustaining long term health after medication",
                    ],
                },
                rhs: {
                    headline: "GLP-1 Without Expert Support",
                    bullets: [
                        "No Medical Oversight — Self-managing dosage without professional monitoring/optimisation",
                        "Unmanaged Side Effects — Trial-and-error approach to nausea, fatigue and complications",
                        "Muscle Mass Loss — Generic calorie restriction without protein optimisation protocols",
                        "Medication-Only Results — Temporary weight loss without behavioural habit formation",
                        "No Transition Strategy — Weight regain after stopping GLP-1 without lifestyle foundation",
                    ],
                },
                disclaimers: [
                    " Coaching does not replace medical care.",
                    "  For side-effect concerns, consult your healthcare provider.",
                    "   Individual outcomes vary. Success depends on adherence and health conditions.",
                ],
            },
            fold5: {
                headline: "Your Complete GLP-1 Care Program ",
                points: [
                    "Improved medication outcomes with expert oversight",
                    "Safe and coordinated care from licensed professionals",
                    "Sustainable lifestyle transformation beyond medication",
                ],
                disclaimers: [
                    " Success depends on individual effort, adherence, and health conditions. Results may vary.",
                ],
                cards: [
                    {
                        heading: "Physician Consultation & Coordination",
                        image: "/images/pro-physician-consultation.png",
                        points: [
                            {
                                title: "Comprehensive Medical Evaluation",
                                description:
                                    "Detailed physician assessment for GLP-1 eligibility and medical safety.",
                            },
                            {
                                title: "Prescription & Treatment Planning",
                                description:
                                    "Evidence-based medication recommendations and therapy planning (if prescribed).",
                            },
                            {
                                title: "Ongoing Medical Optimization",
                                description:
                                    "Continuous follow-up, dose adjustment and therapeutic support.",
                            },
                        ],
                    },
                    {
                        heading: "Coordinated Medication Access",
                        image: "/images/pro-medication-access.png",
                        points: [
                            {
                                title: "Independent Distributor Connections",
                                description:
                                    "Coordination with licensed distributors to streamline medication access.",
                            },
                            {
                                title: "Delivery & Refill Management",
                                description:
                                    "End-to-end support ensuring timely refills and supply continuity.",
                            },
                            {
                                title: "Supply Chain Advocacy",
                                description:
                                    "Proactive assistance resolving medication access barriers and logistic delays.",
                            },
                        ],
                    },
                    {
                        heading: "Comprehensive Lifestyle Integration",
                        image: "/images/pro-lifestyle-integration.png",
                        points: [
                            {
                                title: "GLP-1-Specialized Nutrition Protocols",
                                description:
                                    "Muscle-preserving, culturally tailored nutrition designed for GLP-1 users.",
                            },
                            {
                                title: "WhatsApp-Integrated Guidance",
                                description:
                                    "Real-time communication with dietitian and support team for side effects and nutrition queries.",
                            },
                            {
                                title: "Behavioural Science Coaching",
                                description:
                                    "Evidence-based habit formation for sustainable outcomes post-medication.",
                            },
                        ],
                    },
                    {
                        heading: "Dedicated Care Coordination",
                        image: "/images/pro-care-coordination.png",
                        points: [
                            {
                                title: "Personal Care Navigator",
                                description:
                                    "Single dedicated coordinator aligning physician, nutrition and lifestyle support.",
                            },
                            {
                                title: "Proactive Side-Effect Management",
                                description:
                                    "Interdisciplinary care model addressing medication tolerance and comfort.",
                            },
                            {
                                title: "Post-Therapy Success Planning",
                                description:
                                    "Behavioural strategies ensuring maintenance of results after medication cessation.",
                            },
                        ],
                    },
                ],
            },
        },
    },
};

// ThankYou page state type
export interface ThankYouLocationState {
    email?: string;
    planName?: string | null;
    from?: "payment" | "manual-reset";
    fromMobile?: boolean;
}

export type SubscriptionData = SubscriptionLinkResponse | null | undefined;
export type UserDetails = SubscriptionLinkResponse["user_details"] | undefined;

export interface RazorpayPaymentParams {
    subscriptionData: SubscriptionData;
    userDetails: UserDetails;
    refetch: () => void;
    setShowThankYou: (v: boolean) => void;
    setThankYouState: (v: ThankYouLocationState) => void;
    planName: PlanName | null;
    setSubscriptionLoading?: (v: boolean) => void;
}

// Add simplified version for subscribe queries
export interface SimpleRazorpayPaymentParams {
    subscriptionData: SubscriptionLinkResponse | null | undefined;
    userDetails: SubscriptionLinkResponse["user_details"] | undefined;
    refetch: () => void;
}
